import { UserRolesTranslate } from '@/enums';
import { BaseApiService } from '@/services/base-service';
import { del_mask } from '@/utils';
import {
  ApiPagination, BaseResponse, Response, FiltersPagination, Filters,
} from '@/types';
import {
  InviteUserData, StaffActivityPayload, Staffs,
} from '@/modules/staffs/types';

interface StaffsResponse extends BaseResponse, ApiPagination {
  data: {
    cities: string[];
    items: Staffs.Staff[];
  }
}

interface StaffsCerts extends BaseResponse, ApiPagination {
  data: {
    items: Staffs.StaffCerts[];
    users: Staffs.StaffCertsUsers;
    categories: Staffs.StaffCertsUsersCategories;
    courses: Staffs.StaffCertsUsersCourses;
    cert_validity: string[];
    is_request_courses_not_available: boolean;
  }
}

interface StaffStatistic extends BaseResponse, ApiPagination {
  data: {
    items: Staffs.StaffStatistic[];
  }
}

interface UpdateStaffPayload extends Omit<Staffs.Staff, 'permissions'> {
  permissions?: Record<string, boolean>
}

interface CompatibleRolesList extends BaseResponse {
  data: {
    items: Staffs.CompatibleRoles[];
  }
}

export class StaffsService extends BaseApiService {
  private readonly base_path = 'partner/staff';

  async get_staffs(filters: FiltersPagination<Filters>, all_staff?: boolean, is_active?: boolean): Promise<StaffsResponse> {
    const payload = filters;

    const params: Filters = {
      filters: payload.filters,
      limit: payload.pagination.limit,
      page: payload.pagination.page,
      sort: payload.pagination.sort,
      sort_type: payload.pagination.sort_type,
    };

    if (all_staff) {
      params.all_staff = all_staff;
    }

    if (is_active !== undefined) {
      params.is_active = is_active;
    }

    const { data } = await this.http_client.get<StaffsResponse>(this.base_path, { params });

    return data;
  }

  async get_staff(id: string | number): Promise<Response<Staffs.Staff>> {
    const { data } = await this.http_client.get<Response<Staffs.Staff>>(`${this.base_path}/${id}`);

    return data;
  }

  async send_invite(payload: InviteUserData): Promise<Response<Staffs.Staff>> {
    const { data } = await this.http_client.post<Response<Staffs.Staff>>(this.base_path, payload);

    return data;
  }

  async resending_invite(id: number): Promise<BaseResponse> {
    const { data } = await this.http_client.post<BaseResponse>(`${this.base_path}/${id}/email`);

    return data;
  }

  async deleting_invite(id: number): Promise<BaseResponse> {
    const { data } = await this.http_client.delete<BaseResponse>(`${this.base_path}/${id}`);

    return data;
  }

  async upd_staff(staff: UpdateStaffPayload): Promise<BaseResponse> {
    const payload = staff;

    payload.telephone = del_mask(payload.telephone as string);
    const url = `${this.base_path}/${payload.id}/`;

    const { data } = await this.http_client.patch<BaseResponse>(url, payload);

    return data;
  }

  async toggle_activity_staff(payload: StaffActivityPayload): Promise<BaseResponse> {
    const value = {} as StaffActivityPayload;

    if (typeof payload?.is_veryfied !== 'undefined') {
      value.is_veryfied = payload.is_veryfied;
    }

    if (typeof payload.verification_date !== 'undefined') {
      value.verification_date = payload.verification_date;
    }

    const { data } = await this.http_client.patch<BaseResponse>(
      `${this.base_path}/${payload.id}/activity`,
      value,
    );

    return data;
  }

  async get_certs(filters: FiltersPagination<Filters>): Promise<StaffsCerts> {
    const url = `${this.base_path}/cert`;
    const payload = filters;

    const { data } = await this.http_client.get<StaffsCerts>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_statistics(filters: FiltersPagination<Filters>): Promise<StaffStatistic> {
    const url = `${this.base_path}/cert/statistic`;
    const payload = filters;

    const { data } = await this.http_client.get<StaffStatistic>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async change_role(staff_id: number, code?: number | null) {
    const url = `${this.base_path}/transfer_role`;
    const transfer_stuff = {
      staff_id,
      code,
    };

    const { data } = await this.http_client.post<Staffs.ChangingInfo>(url, transfer_stuff);

    return data;
  }

  async get_compatible_roles(filters: Filters): Promise<CompatibleRolesList> {
    const payload = filters;
    const url = 'roles/compatible';

    const params: Filters = {
      filters: payload.filters,
    };

    const { data } = await this.http_client.get<CompatibleRolesList>(url, { params });

    return data;
  }
}

export const staff_fio = (item: Staffs.Staff) => {
  const name = item?.name ? item.name : '';
  const surname = item?.surname ? item.surname : '';
  const patronymic = item?.patronymic ? item.patronymic : '';

  return `${surname} ${name} ${patronymic}`;
};

export const staff_role = (item: Staffs.Staff) => {
  if (Array.isArray(item?.roles)) {
    return item?.roles.map((e) => UserRolesTranslate[e.slug]).join(', ');
  }
  return '';
};

export interface StaffsFilters extends Filters {
  [key: string]: unknown,
  search: '',
  role: [],
  status: null,
  city: '',
}

export const default_invite = (): InviteUserData => ({
  email: '',
  city: '',
  roles: null,
});
